import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { PQ_TABS, DEFAULT_TAB } from '../schema/pqTabs.js';

// UI layout preferences for the PQ screen. The right insights rail has THREE
// states — full, condensed (slim KPI strip), and hidden (gone, with a discrete
// tab to bring it back) — so an advisor on a cramped laptop can reclaim the
// rail's width entirely. The notes dock along the bottom is open or collapsed
// at a dragged height. Chrome state, not client data. Persisted so the choice
// survives reloads.
//
// There is no left nav any more: the PQ is four tabs (schema/pqTabs.js), and
// the tab bar is the navigation. Which tab is open is remembered per record
// for the session only (below), never across households.
const KEY = 'pq.layoutPrefs.v1';

// Two width bands drive the rail automatically on narrow screens:
//  - at/below SQUEEZE_MAX the rail auto-CONDENSES (slim), so the rail open on
//    a ~1280-1366px laptop can't force a horizontal scrollbar (R18);
//  - at/below HIDE_MAX the rail auto-HIDES — this is roughly the width where the
//    wide tables start demoting columns behind the detail arrow, so hiding the
//    rail hands that width back to the form (Eugene, 7/9). Viewport-driven (not
//    keyed off the tables' own compaction) to avoid a hide↔compact feedback
//    loop. Either way an explicit choice overrides until the window widens out.
const SQUEEZE_MAX = 1340;
const HIDE_MAX = 1200;
// At/below ZOOM_MAX the PQ switches to Zoom mode on its own. Advisors run the
// PQ in a half-width window during live client meetings, with Zoom or Teams on
// the other half (David Kleinman and Jeff Wille, 8/13; Kyle Farmer, 9/4). Half
// of a 1920 monitor is 960px, half of a 1366 laptop is 683px. Zoom mode is the
// form alone under a shorter topbar, with the rail gone and its three figures
// folded into the tab bar instead. Same override rule as the rail: an explicit
// choice inside the band holds until the window widens.
const ZOOM_MAX = 1000;
// Notes dock defaults. The height is the open body only (the collapsed bar is
// CSS-sized); bounded so a drag cannot shrink it to nothing or swallow the form.
const DOCK_DEFAULT = 180;
const DOCK_MIN = 90;
const DOCK_MAX_VH = 0.6;

const RAIL_MODES = ['full', 'condensed', 'hidden'];
const TAB_IDS = PQ_TABS.map(t => t.id);

function getWidth() {
  return typeof window !== 'undefined' ? window.innerWidth : 1920;
}

// The open tab is kept per record in sessionStorage: a reload mid-meeting lands
// where the advisor was, and opening a different household (or a new one) in
// the same tab starts at the first page as it should.
function tabKey() {
  try {
    const q = new URLSearchParams(window.location.search);
    const rec = q.get('leadId') || q.get('householdId') || q.get('pqId') || 'new';
    return `pq.tab.${rec}`;
  } catch {
    return 'pq.tab.new';
  }
}
function loadTab() {
  try {
    const t = sessionStorage.getItem(tabKey());
    if (TAB_IDS.includes(t)) return t;
  } catch { /* blocked storage */ }
  return DEFAULT_TAB;
}

const LayoutContext = createContext(null);

function loadPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw);
      // Back-compat: earlier builds stored a boolean `railCollapsed`.
      const railMode = RAIL_MODES.includes(p.railMode)
        ? p.railMode
        : (p.railCollapsed === false ? 'full' : 'condensed');
      // The dock used to exist only in Zoom mode, under zoomNotes* keys. A
      // stored choice carries over so nobody's open notes close on them.
      const notesOpen = typeof p.notesOpen === 'boolean'
        ? p.notesOpen
        : (typeof p.zoomNotesOpen === 'boolean' ? p.zoomNotesOpen : false);
      const height = Number.isFinite(p.notesHeight) ? p.notesHeight : p.zoomNotesHeight;
      return {
        railMode,
        zoomMode: !!p.zoomMode,
        notesOpen,
        // A number that is not a number falls back rather than producing a
        // NaN-high dock.
        notesHeight: Number.isFinite(height) ? height : DOCK_DEFAULT,
      };
    }
  } catch {
    /* ignore malformed/blocked storage */
  }
  // Default: rail condensed so the form gets width out of the box while the
  // KPI strip stays a glance away; notes collapsed to their bar until asked.
  return {
    railMode: 'condensed',
    zoomMode: false,
    notesOpen: false,
    notesHeight: DOCK_DEFAULT,
  };
}

export function LayoutProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [activeTab, setActiveTabState] = useState(loadTab);
  const [narrow, setNarrow] = useState(() => getWidth() <= SQUEEZE_MAX);
  const [veryNarrow, setVeryNarrow] = useState(() => getWidth() <= HIDE_MAX);
  const [zoomBand, setZoomBand] = useState(() => getWidth() <= ZOOM_MAX);
  // Zoom mode forced on or off while inside the zoom band: null = the band
  // default (on). Cleared once the window widens out, like railOverride.
  const [zoomOverride, setZoomOverride] = useState(null);
  // The rail mode the user forced while in an auto band: null = follow the
  // band default. Cleared once the viewport widens past the squeeze band so the
  // stored wide-screen preference governs again.
  const [railOverride, setRailOverride] = useState(null);

  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const overrideRef = useRef(railOverride);
  overrideRef.current = railOverride;

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      /* ignore blocked storage */
    }
  }, [prefs]);

  useEffect(() => {
    try { sessionStorage.setItem(tabKey(), activeTab); } catch { /* blocked storage */ }
  }, [activeTab]);

  useEffect(() => {
    const onResize = () => {
      const w = getWidth();
      setNarrow(w <= SQUEEZE_MAX);
      setVeryNarrow(w <= HIDE_MAX);
      setZoomBand(w <= ZOOM_MAX);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Once the window widens past the squeeze band, drop any narrow-only override.
  useEffect(() => {
    if (!narrow) setRailOverride(null);
  }, [narrow]);
  useEffect(() => {
    if (!zoomBand) setZoomOverride(null);
  }, [zoomBand]);

  // Switching tabs also returns to the top: the new page starts at its first
  // section, under the sticky chrome, rather than wherever the last one was
  // scrolled to. An unknown id is ignored rather than blanking the form.
  const setActiveTab = useCallback(id => {
    if (!TAB_IDS.includes(id)) return;
    setActiveTabState(prev => {
      if (prev !== id && typeof window !== 'undefined') window.scrollTo({ top: 0 });
      return id;
    });
  }, []);

  // Effective rail mode: hide band → hidden, squeeze band → condensed, else the
  // stored preference. An explicit override wins inside a band.
  const railMode = veryNarrow
    ? (railOverride ?? 'hidden')
    : narrow
      ? (railOverride ?? 'condensed')
      : prefs.railMode;

  // Set the rail mode at the right level: an override while narrow, the stored
  // preference when wide.
  const setRailMode = useCallback(mode => {
    if (getWidth() <= SQUEEZE_MAX) setRailOverride(mode);
    else setPrefs(p => ({ ...p, railMode: mode }));
  }, []);

  // The current effective mode, read live for the toggles below so they flip
  // correctly whether it came from a preference or a band default.
  const effectiveRail = useCallback(() => {
    const w = getWidth();
    return w <= HIDE_MAX
      ? (overrideRef.current ?? 'hidden')
      : w <= SQUEEZE_MAX
        ? (overrideRef.current ?? 'condensed')
        : prefsRef.current.railMode;
  }, []);

  // The chevron flips between full and condensed (never hides — hiding is the
  // explicit × / tab).
  const toggleRail = useCallback(() => {
    setRailMode(effectiveRail() === 'full' ? 'condensed' : 'full');
  }, [setRailMode, effectiveRail]);

  const hideRail = useCallback(() => setRailMode('hidden'), [setRailMode]);
  const showRail = useCallback(() => setRailMode('condensed'), [setRailMode]);

  // Focus mode = the form alone: the rail hidden. With the side nav gone the
  // rail is the only panel left to clear, so Focus is the one-click version of
  // the rail's own × and the "Insights" tab brings it back either way.
  const focusMode = railMode === 'hidden';
  const toggleFocus = useCallback(() => {
    setRailMode(effectiveRail() === 'hidden' ? 'condensed' : 'hidden');
  }, [setRailMode, effectiveRail]);

  // Effective Zoom mode: the band default (on) unless overridden inside the
  // band, otherwise the stored preference. Mirrors the rail so the two never
  // disagree about what "the window got narrower" means.
  const zoomMode = zoomBand ? (zoomOverride ?? true) : prefs.zoomMode;
  const toggleZoom = useCallback(() => {
    if (getWidth() <= ZOOM_MAX) {
      setZoomOverride(o => !(o ?? true));
    } else {
      setPrefs(p => ({ ...p, zoomMode: !p.zoomMode }));
    }
  }, []);

  const notesOpen = prefs.notesOpen;
  const setNotesOpen = useCallback(
    open => setPrefs(p => ({ ...p, notesOpen: !!open })),
    []
  );
  const notesHeight = prefs.notesHeight;
  const setNotesHeight = useCallback(px => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
    const max = Math.max(DOCK_MIN, Math.round(vh * DOCK_MAX_VH));
    const next = Math.min(max, Math.max(DOCK_MIN, Math.round(px)));
    setPrefs(p => (p.notesHeight === next ? p : { ...p, notesHeight: next }));
  }, []);

  const value = {
    activeTab,
    setActiveTab,
    railMode,
    railCollapsed: railMode === 'condensed',
    railHidden: railMode === 'hidden',
    focusMode,
    toggleRail,
    hideRail,
    showRail,
    toggleFocus,
    zoomMode,
    toggleZoom,
    notesOpen,
    setNotesOpen,
    notesHeight,
    setNotesHeight,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayout() {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayout must be used within a LayoutProvider');
  return ctx;
}
